'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';

// Synopsis keyword chips. On mount we detect which keywords actually appear in
// the page body ([data-page-body]); those become buttons that smooth-scroll to
// (and briefly flash) the first occurrence. Keywords not in the text render as
// plain, non-interactive chips.
export const KeywordChips: React.FC<{ keywords: string[]; className?: string; chipClassName?: string }> = ({
  keywords,
  className,
  chipClassName,
}) => {
  const [present, setPresent] = useState<Record<string, boolean>>({});
  const bodyRef = useRef<HTMLElement | null>(null);

  const uniq = useMemo(
    () => Array.from(new Set(keywords.map((k) => k.trim()).filter((k) => k.length >= 2))),
    [keywords],
  );

  useEffect(() => {
    const body = document.querySelector<HTMLElement>('[data-page-body]');
    bodyRef.current = body;
    if (!body) return;
    const text = (body.textContent || '').toLowerCase();
    const found: Record<string, boolean> = {};
    for (const k of uniq) if (text.includes(k.toLowerCase())) found[k] = true;
    setPresent(found);
  }, [uniq]);

  const jumpTo = (kw: string) => {
    const body = bodyRef.current || document.querySelector<HTMLElement>('[data-page-body]');
    if (!body) return;
    const needle = kw.toLowerCase();
    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const val = node.nodeValue || '';
      const idx = val.toLowerCase().indexOf(needle);
      if (idx >= 0) {
        try {
          const range = document.createRange();
          range.setStart(node, idx);
          range.setEnd(node, Math.min(idx + kw.length, val.length));
          const rect = range.getBoundingClientRect();
          window.scrollTo({ top: window.scrollY + rect.top - 130, behavior: 'smooth' });
          const parent = (node.parentElement as HTMLElement) || null;
          if (parent) {
            parent.classList.add('kwFlash');
            setTimeout(() => parent.classList.remove('kwFlash'), 2200);
          }
        } catch {
          (node.parentElement as HTMLElement)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
    }
  };

  if (uniq.length === 0) return null;
  return (
    <div className={className}>
      {uniq.map((k, i) =>
        present[k] ? (
          <button
            key={i}
            type="button"
            className={chipClassName}
            style={{ cursor: 'pointer', border: 'none', font: 'inherit' }}
            title={`Jump to "${k}" in the text`}
            onClick={() => jumpTo(k)}
          >
            {k}
          </button>
        ) : (
          <span key={i} className={chipClassName}>{k}</span>
        ),
      )}
    </div>
  );
};

export default KeywordChips;
